import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { ReCaptchaCheckbox } from '@/Domain/components/Auth/ReCaptchaCheckbox';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { Input } from '@/Domain/components/Shared/Input/Input';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import { EnvelopIcon } from '@/Domain/components/icons/Envelop.icon';
import { useForgotPasswordHook } from '@/Domain/hooks/Auth/useForgotPassword.hook';

// TODO: try do remove recaptcha V2
export const ForgotPasswordPage = () => {
  const { t } = useTranslation(['forgot_password, forgot_password_success']);

  const navigate = useNavigate();
  const { track } = useAnalytics();

  const { register, errors, control, handleSubmit, useV2, forgot, onSubmit, onVerify } =
    useForgotPasswordHook();

  const handleClick = () => {
    track('forgot_password_back_success_click', {
      event_category: 'Forgot password',
      event_label: 'forgot_password_form',
    });

    navigate('/', { viewTransition: true });
  };

  if (forgot.isSuccess) {
    return (
      <div className="animate-in fade-in duration-300">
        {/* Success State */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-green-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </div>
          <h1 className="font-semibold text-2xl text-gray-950 mb-2">
            {t('forgot_password_success:title')}
          </h1>
        </div>
        <p className="text-gray-600 text-center mb-6">{t('forgot_password_success:description')}</p>
        <Button variant="dark" size="lg" className="w-full" onClick={handleClick}>
          {t('forgot_password_success:link')}
        </Button>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-300">
      {/* Header */}
      <div className="text-center mb-6">
        <h1 className="font-semibold text-2xl text-gray-950 mb-2">{t('forgot_password:title')}</h1>
        <p className="text-gray-500 text-sm">{t('forgot_password:subtitle')}</p>
      </div>

      <div className="grid gap-5">
        {/* Form */}
        <form
          id="forgot-password-form"
          className="flex flex-col gap-5"
          onSubmit={handleSubmit(onSubmit)}
        >
          <GoogleReCaptcha onVerify={onVerify} />

          {/* Email Field */}
          <div className="space-y-1.5">
            <label htmlFor="email" className="text-sm font-medium text-gray-700">
              {t('forgot_password:form.email')}
            </label>
            <Input
              id="email"
              size="lg"
              placeholder={t('forgot_password:form.email_placeholder')}
              error={!!errors.email}
              autoFocus
              autoComplete="email"
              startIcon={<EnvelopIcon className="h-4 w-4 text-gray-400" />}
              {...register('email')}
            />
            {errors.email && (
              <p className="text-sm text-red-600 flex items-center gap-1 animate-in fade-in slide-in-from-top-1 duration-200">
                {errors.email.message}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            variant="dark"
            size="lg"
            type="submit"
            disabled={forgot.isPending}
            className="w-full mt-1"
          >
            {forgot.isPending ? <Spinner /> : t('forgot_password:button.submit')}
          </Button>

          {/* reCAPTCHA v2 Fallback */}
          {useV2 && (
            <Controller
              name="recaptcha_response_v2"
              control={control}
              rules={{
                required: true,
              }}
              render={({ field: { onChange }, fieldState: { invalid } }) => (
                <ReCaptchaCheckbox onChange={onChange} isInvalid={invalid} />
              )}
            />
          )}
        </form>

        {/* Back to Login Link */}
        <div className="text-center pt-4 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            {t('forgot_password:back_prompt')}{' '}
            <Link
              to="/"
              className="text-gray-700 hover:text-black font-medium transition-colors hover-underline"
              onClick={() =>
                track('forgot_password_back_click', {
                  event_category: 'Forgot password',
                  event_label: 'forgot_password_form',
                })
              }
            >
              {t('forgot_password:link.login')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
